import ReportHeader from "../../shared/components/ReportHeader";
import React, {useMemo, useRef} from "react";
import Box from "@mui/material/Box";
import moment from "moment";
import Typography from "@mui/material/Typography";
import BoxContent from "../../../../shared/ui/BoxContent";
import ScrollBar from "../../../../shared/ui/ScrollBar";
import {calculateTableRows, getSubcategories, sortStudents} from "../../shared/utils/functions";
import {generateHeatMapData} from "../../../../utils/functions";
import CustomTableBlack from "../../../../shared/tables/CustomTableBlack";
import ReportHeatMap from "./ReportHeatMap";
import ReportDescription from "./ReportDescription";

export interface Student {
    student_name: string;
    subcategories: { subcategory_name: string; subcategory_score: number }[];
}
export interface ReportData {
    id: string;
    score: number;
    start_date: Date;
    end_date: Date;
    subject: string;
    tag: any[];
    payload: {
        classroom_grade: string;
        classroom_name: string;
        students: Student[];
    };
}

export interface TableRow {
    dimensions: string;
    students_number: number;
    students: string[];
}

const CELL_SIZE = 60;

const GetHeatmapDataClasses = (isStandard: boolean) => {
    if (isStandard) {
        return [
            {
                from: 0,
                to: 5,
                color: '#FCE8EA',
                name: 'Performanță scăzută (C: ≤ 5)',
            },
            {
                from: 5,
                to: 7,
                color: '#D7E6FF',
                name: 'Nivel mediu (B: 5–7)',
            },
            {
                from: 7,
                to: 10,
                color: '#d0fce6',
                name: 'Performanță ridicată (A: > 7)',
            },
        ];
    } else {
        return [
            {
                from: 0,
                to: 51,
                color: '#FCE8EA',
                name: 'Performanță scăzută (D, E: ≤ 51)',
            },
            {
                from: 51,
                to: 70,
                color: '#D7E6FF',
                name: 'Nivel mediu (C: 51–70)',
            },
            {
                from: 70,
                to: 100,
                color: '#d0fce6',
                name: 'Performanță ridicată (A, B: > 70)',
            },
        ];
    }
}

export const TABLE_COLUMNS_STANDARD = [
    {label: "Conținut", id: "dimensions", noWrap: false},
    {label: "Numar elevi", id: "students_number", numeric: true,},
    {label: "Elevi", id: "students", numeric: false, noWrap: true},
    {label: "Notă", id: "studentScore", numeric: true,}
];

export const TABLE_COLUMNS = [
    {label: "Dimensiune", id: "dimensions", noWrap: false},
    {label: "Numar elevi", id: "students_number", numeric: true,},
    {label: "Elevi", id: "students", numeric: false, noWrap: true},
    {label: "Scor", id: "studentScore", numeric: true,}
];

const ClassroomStandardsReport = ({report, isPrinting = false, isStandard = false} : {report: ReportData, isPrinting?: boolean, isStandard?: boolean}) => {
    const sortedStudents = sortStudents(report.payload.students);
    const sortedHeatmapData = generateHeatMapData(sortedStudents, "studentScore");
    const studentNames = sortedStudents.map(student => student.student_name.substring(0, 40));
    const subcategories = getSubcategories(sortedStudents);
    const tableRows = calculateTableRows(sortedStudents, subcategories, isStandard);

    const formattedDate = useMemo(() => {
        if (!report.start_date && !report.end_date) return "";
        return `${moment(report.start_date).locale("ro").format("DD.MM.YYYY")} - ${moment(report.end_date).locale("ro").format("DD.MM.YYYY")}`;
    }, [report.start_date, report.end_date]);

    const dynamicHeight = (subcategories.length * CELL_SIZE) + 250;
    const dynamicWidth = (studentNames.length * CELL_SIZE) + 450;
    const dynamicHeightInverted = (studentNames.length * 20) + 250;
    const dynamicWidthInverted = (subcategories.length * 40) + 450;

    return (
        <Box mb={5}>
            <ReportHeader
                locationUrl="rapoarte-clasa"
                reportId={report.id}
                score={report.score}
                testDate={formattedDate}
                testTypeId={1}
                subjectName={report.subject}
                className={`(${report.payload.classroom_grade}) ${report.payload.classroom_name} `}
            />

            <ReportDescription report={report} isStandard={isStandard}/>

            <Typography variant="h1" mb={2} mt={4}>
                Rezultate privind competențele măsurate, pentru fiecare elev
            </Typography>

            <Box sx={{
                p: {xs: 1.5, md: 2, lg: 2, xl: 3},
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "background.paper",
                breakAfter: "always",
                pageBreakAfter: "always"
            }}>
                {isPrinting ? (
                    <Box sx={{minWidth: dynamicWidthInverted}}>
                        <ReportHeatMap
                            dynamicWidth={dynamicWidthInverted}
                            dynamicHeight={dynamicHeightInverted}
                            sortedHeatmapData={sortedHeatmapData}
                            studentNames={studentNames}
                            subcategories={subcategories}
                            isInverted = {true}
                            dataClasses={GetHeatmapDataClasses(isStandard)}
                            isStandard={isStandard}
                        />
                    </Box>
                ) : (
                    <ScrollBar forceVisible="x" autoHide={false}  >
                        <Box sx={{minWidth: dynamicWidth}}>
                            <ReportHeatMap
                                dynamicWidth={dynamicWidth}
                                dynamicHeight={dynamicHeight}
                                sortedHeatmapData={sortedHeatmapData}
                                studentNames={studentNames}
                                subcategories={subcategories}
                                dataClasses={GetHeatmapDataClasses(isStandard)}
                                isStandard={isStandard}
                            />
                        </Box>
                    </ScrollBar>
                )}
            </Box>

            {isStandard ? (
                <Typography variant="h1" mb={2} mt={4}>
                    Tabelul 1. Elevii aflați în categoria C pe fiecare conținut
                </Typography>
            ) : (
                <Typography variant="h1" mb={2} mt={4}>
                    Tabelul 1. Elevii aflați în categoriile E și D pe fiecare capitol
                </Typography>
            )}

            <BoxContent color="light" dashboardBox={true} sxExtra={{p: -3, py: 3}}>
                <CustomTableBlack
                    data={
                        {
                            columns: isStandard ? TABLE_COLUMNS_STANDARD : TABLE_COLUMNS,
                            rows: tableRows,
                        }
                    }
                    extraPadding={"0 0 0 60px"}
                    bgColor="light"
                    hasTooltip={false}
                />
            </BoxContent>
        </Box>
    )
}

export default ClassroomStandardsReport;